import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TradingViewChart } from '@/components/charts/TradingViewChart'
import { useMarket } from '@/contexts/MarketContext'
import { formatPercentChange } from '@/lib/format'
import { cn } from '@/lib/cn'
import { OrderTicket } from './OrderTicket'
import { SymbolInfoPanel } from './SymbolInfoPanel'

/**
 * Mobile / PWA Trade screen: full-bleed chart + toggleable Buy/Sell sheet.
 */
export function MobileTradeView() {
  const { t, i18n } = useTranslation(['trading', 'common'])
  const { active } = useMarket()
  const [ticketOpen, setTicketOpen] = useState(false)
  const up = active.changePct >= 0

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <div className="flex h-10 shrink-0 items-center justify-between gap-2 border-b border-terminal-border/40 px-3">
        <div className="flex min-w-0 items-baseline gap-2">
          <h1 className="truncate font-mono text-sm font-semibold tabular-nums">
            {active.label}
          </h1>
          <span
            className={cn(
              'rounded-sm px-1.5 py-0.5 font-mono text-[10px] tabular-nums',
              up ? 'bg-bid-badge text-bid' : 'bg-ask-badge text-ask',
            )}
          >
            {formatPercentChange(active.changePct)}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setTicketOpen((o) => !o)}
          className={cn(
            'shrink-0 rounded-sm px-2.5 py-1.5 text-[11px] font-semibold',
            ticketOpen
              ? 'bg-terminal-elevated text-terminal-fg'
              : 'bg-bid text-white',
          )}
          aria-expanded={ticketOpen}
        >
          {ticketOpen
            ? t('orderTicket.closeTicket')
            : t('orderTicket.openTicket')}
        </button>
      </div>

      <div className="min-h-0 flex-1">
        <TradingViewChart
          symbol={active.tvSymbol}
          interval="15"
          locale={i18n.language}
        />
      </div>

      {ticketOpen ? (
        <>
          <button
            type="button"
            className="absolute inset-0 z-20 bg-black/40"
            aria-label={t('orderTicket.closeTicket')}
            onClick={() => setTicketOpen(false)}
          />
          <div
            className={cn(
              'absolute inset-x-0 bottom-0 z-30 max-h-[75%] overflow-y-auto',
              'rounded-t-xl border-t border-terminal-border/40 bg-terminal-surface shadow-2xl',
              'pb-[env(safe-area-inset-bottom)]',
            )}
            role="dialog"
            aria-label={t('orderTicket.title')}
          >
            <div className="flex justify-center py-2">
              <span className="h-1 w-10 rounded-full bg-terminal-border" />
            </div>
            <OrderTicket />
            <SymbolInfoPanel />
          </div>
        </>
      ) : null}
    </div>
  )
}
